import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Trash2, CalendarDays, Phone, Mail, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { APPOINTMENT_TYPE_LABELS, TIME_SLOT_LABELS, APPOINTMENT_STATUSES, toDateStr } from "@/data/appointments";
import { EmptyState } from "@/components/admin/AdminBits";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function MonthGrid({ monthDate, byDate, selectedDate, onSelect }) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = toDateStr(new Date());
  const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  return (
    <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200" data-testid="appointments-month-grid">
      {WEEKDAYS.map((w) => (
        <div key={w} className="bg-slate-50 text-center py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{w}</div>
      ))}
      {cells.map((day, i) => {
        if (!day) return <div key={`e-${i}`} className="bg-white h-20" />;
        const ds = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const count = (byDate[ds] || []).length;
        const isSelected = ds === selectedDate;
        return (
          <button
            key={ds}
            onClick={() => onSelect(ds)}
            data-testid={`cal-day-${ds}`}
            className={`h-20 p-2 text-left transition-colors flex flex-col justify-between ${
              isSelected ? "bg-primary-brand text-white" : "bg-white hover:bg-orange-50"
            }`}
          >
            <span className={`text-sm font-bold ${ds === todayStr && !isSelected ? "text-primary-brand" : ""}`}>{day}</span>
            {count > 0 && (
              <span className={`self-end text-[10px] font-black px-1.5 py-0.5 ${isSelected ? "bg-white text-primary-brand" : "bg-primary-brand text-white"}`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function DayList({ items, onStatusChange, onDelete }) {
  if (items.length === 0) return <EmptyState label="No appointments on this day." />;
  return (
    <div className="border border-slate-200 bg-white divide-y divide-slate-100" data-testid="appointments-day-list">
      {items.map((a) => (
        <div key={a.id} data-testid={`appointment-row-${a.id}`} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="w-24 flex-none">
            <div className="font-heading font-black text-primary-brand">{TIME_SLOT_LABELS[a.time_slot] || a.time_slot}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{APPOINTMENT_TYPE_LABELS[a.appointment_type] || a.appointment_type}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-slate-900">{a.name}</div>
            <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1 mt-0.5">
              <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{a.email}</span>
              <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{a.phone}</span>
              {a.reference && <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{a.reference}</span>}
            </div>
            {a.notes && <div className="text-xs text-slate-600 mt-1 truncate" title={a.notes}>{a.notes}</div>}
          </div>
          <div className="flex items-center gap-2 flex-none">
            <select
              value={a.status}
              onChange={(e) => onStatusChange(a.id, e.target.value)}
              data-testid={`appointment-status-${a.id}`}
              className="bg-transparent border border-slate-200 px-2 py-1.5 text-xs uppercase tracking-wider"
            >
              {APPOINTMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={() => onDelete(a.id)} data-testid={`appointment-delete-${a.id}`} className="text-slate-500 hover:text-red-500 p-1">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function AppointmentsPanel() {
  const [monthDate, setMonthDate] = useState(() => new Date());
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => toDateStr(new Date()));

  const mk = monthKey(monthDate);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/appointments?month=${mk}`);
      setAppointments(data);
    } catch (error) {
      console.error("Appointments load failed:", error);
      toast.error("Failed to load appointments");
    }
  }, [mk]);

  useEffect(() => { load(); }, [load]);

  const byDate = appointments.reduce((acc, a) => {
    (acc[a.date] = acc[a.date] || []).push(a);
    return acc;
  }, {});

  const changeMonth = (delta) => {
    const d = new Date(monthDate.getFullYear(), monthDate.getMonth() + delta, 1);
    setMonthDate(d);
    setSelectedDate(`${monthKey(d)}-01`);
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      setAppointments((ls) => ls.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success("Appointment updated");
    } catch (error) {
      console.error("Appointment update failed:", error);
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await api.delete(`/admin/appointments/${id}`);
      setAppointments((ls) => ls.filter((a) => a.id !== id));
      toast.success("Deleted");
    } catch (error) {
      console.error("Appointment delete failed:", error);
      toast.error("Delete failed");
    }
  };

  const monthLabel = monthDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const dayItems = (byDate[selectedDate] || []).slice().sort((a, b) => a.time_slot.localeCompare(b.time_slot));

  return (
    <div data-testid="appointments-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-primary-brand" />
          <h2 className="font-heading text-xl font-black text-slate-900">{monthLabel}</h2>
          <span className="text-xs text-slate-500">({appointments.length} this month)</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => changeMonth(-1)} data-testid="cal-prev-month" className="p-2 border border-slate-200 hover:border-primary-brand text-slate-600 hover:text-primary-brand">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => changeMonth(1)} data-testid="cal-next-month" className="p-2 border border-slate-200 hover:border-primary-brand text-slate-600 hover:text-primary-brand">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <MonthGrid monthDate={monthDate} byDate={byDate} selectedDate={selectedDate} onSelect={setSelectedDate} />

      <div>
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-primary-brand mb-3">
          Schedule for {selectedDate}
        </div>
        <DayList items={dayItems} onStatusChange={updateStatus} onDelete={remove} />
      </div>
    </div>
  );
}
